import React, { useState, useEffect } from 'react';
import { getMember, getMemberIdCard, uploadMemberPhoto, updateMember, approveMember, updateMemberExpiry, changeMemberSource, deleteMember, restoreMember } from '../services/api';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

function MemberDetailModal({ memberId, onClose }) {
  const [member, setMember] = useState(null);
  const [idCardUrl, setIdCardUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [expiryInput, setExpiryInput] = useState('');
  const [sourceInput, setSourceInput] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const userRole = localStorage.getItem('role');

  const loadMember = () => {
    getMember(memberId).then(res => {
      setMember(res.data);
      setForm({
        name: res.data.name || '', civilIdNo: res.data.civilIdNo || '',
        gender: res.data.gender || '', mobileNo: res.data.mobileNo || '',
        email: res.data.email || '', bloodGroup: res.data.bloodGroup || '',
        nativePlace: res.data.nativePlace || ''
      });
      if (res.data.expiryDate) {
        setExpiryInput(res.data.expiryDate.substring(0, 16));
      }
      setSourceInput(res.data.utmSource || '');
    });
    getMemberIdCard(memberId).then(res => {
      setIdCardUrl(URL.createObjectURL(res.data));
    }).catch(() => {});
  };

  useEffect(() => {
    if (memberId) loadMember();
    return () => { if (idCardUrl) URL.revokeObjectURL(idCardUrl); };
  }, [memberId]);

  const handlePhotoReplace = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadMemberPhoto(memberId, file);
      loadMember();
    } catch { alert('Failed to update photo'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMember(memberId, form);
      setEditing(false);
      loadMember();
    } catch { alert('Failed to update member'); }
    finally { setSaving(false); }
  };

  const handleDownload = () => {
    if (!idCardUrl) return;
    const a = document.createElement('a');
    a.href = idCardUrl;
    a.download = `idcard_${member?.civilIdNo || 'member'}.png`;
    a.click();
  };

  const handleApprove = async () => {
    try {
      await approveMember(memberId, { username: localStorage.getItem('username') });
      loadMember();
    } catch { alert('Failed to approve member'); }
  };

  const handleExpiryUpdate = async () => {
    if (!expiryInput) return;
    try {
      await updateMemberExpiry(memberId, { expiryDate: expiryInput + ':00' });
      loadMember();
    } catch { alert('Failed to update expiry'); }
  };

  const handleMarkDeleted = async () => {
    if (!window.confirm(`Mark "${member.name}" as deleted? They will be hidden from the list and verification will be blocked until restored.`)) return;
    setDeleteBusy(true);
    try {
      await deleteMember(memberId, localStorage.getItem('username') || '');
      loadMember();
    } catch { alert('Failed to mark as deleted'); }
    finally { setDeleteBusy(false); }
  };

  const handleRestore = async () => {
    if (!window.confirm(`Restore "${member.name}"?`)) return;
    setDeleteBusy(true);
    try {
      await restoreMember(memberId);
      loadMember();
    } catch { alert('Failed to restore'); }
    finally { setDeleteBusy(false); }
  };

  const handleSourceChange = async () => {
    if (!sourceInput.trim() || sourceInput === member.utmSource) return;
    try {
      await changeMemberSource(memberId, sourceInput, localStorage.getItem('username'));
      loadMember();
    } catch { alert('Failed to change source'); }
  };

  if (!member) return null;

  const inputStyle = {padding:'6px 8px',border:'1px solid #ccc',borderRadius:'4px',fontSize:'14px',width:'100%',boxSizing:'border-box'};
  const selectStyle = {...inputStyle, backgroundColor:'#fff'};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Member Details</h2>
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
            {!editing ? (
              <button onClick={() => setEditing(true)} style={{padding:'6px 14px',backgroundColor:'#ff9800',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px'}}>✏️ Edit</button>
            ) : (
              <>
                <button onClick={handleSave} disabled={saving} style={{padding:'6px 14px',backgroundColor:'#4caf50',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px',opacity:saving?0.7:1}}>{saving ? '⏳ Saving...' : '💾 Save'}</button>
                <button onClick={() => { setEditing(false); setForm({name:member.name||'',civilIdNo:member.civilIdNo||'',gender:member.gender||'',mobileNo:member.mobileNo||'',email:member.email||'',bloodGroup:member.bloodGroup||'',nativePlace:member.nativePlace||''}); }} style={{padding:'6px 14px',backgroundColor:'#9e9e9e',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px'}}>Cancel</button>
              </>
            )}
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="photo-section">
              {member.photoBase64 ? (
                <img src={`data:image/jpeg;base64,${member.photoBase64}`} alt={member.name} className="student-photo" />
              ) : (
                <div className="no-photo"><span>No Photo</span></div>
              )}
              <label style={{display:'inline-block',marginTop:'8px',padding:'6px 12px',backgroundColor:'#1565c0',color:'#fff',borderRadius:'4px',cursor: uploading ? 'wait' : 'pointer',fontSize:'12px',opacity: uploading ? 0.7 : 1}}>
                {uploading ? '⏳ Uploading...' : '📷 Replace Photo'}
                <input type="file" accept="image/*" onChange={handlePhotoReplace} disabled={uploading} style={{display:'none'}} />
              </label>
            </div>
            <div className="info-section">
              {editing ? (
                <>
                  <div className="info-row"><label>Name:</label><input style={inputStyle} value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                  <div className="info-row"><label>Civil ID No.:</label><input style={inputStyle} value={form.civilIdNo} onChange={e => setForm({...form, civilIdNo: e.target.value})} /></div>
                  <div className="info-row"><label>Gender:</label>
                    <select style={selectStyle} value={form.gender} onChange={e => setForm({...form, gender: e.target.value})}>
                      <option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="info-row"><label>Mobile No.:</label><input style={inputStyle} value={form.mobileNo} onChange={e => setForm({...form, mobileNo: e.target.value})} /></div>
                  <div className="info-row"><label>Email:</label><input style={inputStyle} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                  <div className="info-row"><label>Blood Group:</label>
                    <select style={selectStyle} value={form.bloodGroup} onChange={e => setForm({...form, bloodGroup: e.target.value})}>
                      <option value="">Select</option>{bloodGroups.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                    </select>
                  </div>
                  <div className="info-row"><label>Native (Place/Country):</label><input style={inputStyle} value={form.nativePlace} onChange={e => setForm({...form, nativePlace: e.target.value})} /></div>
                </>
              ) : (
                <>
                  <div className="info-row"><label>Name:</label><span>{member.name}</span></div>
                  <div className="info-row"><label>Civil ID No.:</label><span>{member.civilIdNo}</span></div>
                  <div className="info-row"><label>Gender:</label><span>{member.gender}</span></div>
                  <div className="info-row"><label>Mobile No.:</label><span>{member.mobileNo}</span></div>
                  <div className="info-row"><label>Email:</label><span>{member.email || '-'}</span></div>
                  <div className="info-row"><label>Blood Group:</label><span style={{fontWeight: 'bold', color: '#d32f2f'}}>{member.bloodGroup}</span></div>
                  <div className="info-row"><label>Native (Place/Country):</label><span>{member.nativePlace}</span></div>
                </>
              )}
              <div className="info-row"><label>Submitted At:</label><span>{member.submittedAt ? new Date(member.submittedAt).toLocaleString() : '-'}</span></div>
              {member.utmSource && (
                <div className="info-row"><label>Source:</label><span style={{fontWeight: 'bold', color: '#2d5f3f'}}>{member.utmSource}</span></div>
              )}
              {userRole === 'MASTER' && (
                <div className="info-row" style={{alignItems:'center'}}>
                  <label>Change Source:</label>
                  <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                    <input style={{...inputStyle,width:'auto',flex:1}} value={sourceInput} onChange={e => setSourceInput(e.target.value)} placeholder={member.utmSource || 'Enter source'} />
                    <button onClick={handleSourceChange} disabled={!sourceInput.trim() || sourceInput === member.utmSource}
                      style={{padding:'6px 14px',backgroundColor:'#e65100',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px',whiteSpace:'nowrap',opacity:(!sourceInput.trim()||sourceInput===member.utmSource)?0.5:1}}>
                      Update Source
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Approval Section */}
          {(userRole === 'ADMIN' || userRole === 'MASTER') && (
            <div style={{marginTop:'20px',borderTop:'1px solid #ddd',paddingTop:'20px'}}>
              <h3 style={{margin:'0 0 12px'}}>Membership Approval</h3>
              <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
                <span style={{padding:'6px 14px',borderRadius:'4px',fontSize:'13px',fontWeight:'bold',
                  background: member.approved ? '#4caf50' : '#ff9800', color:'#fff'}}>
                  {member.approved ? '✅ Approved' : '⏳ Pending Approval'}
                </span>
                {!member.approved && (
                  <button onClick={handleApprove} style={{padding:'6px 14px',backgroundColor:'#4caf50',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px'}}>
                    Approve Member
                  </button>
                )}
              </div>
              {member.approved && (
                <div style={{marginTop:'12px',display:'flex',flexDirection:'column',gap:'8px'}}>
                  <div className="info-row"><label>Approved By:</label><span>{member.approvedBy}</span></div>
                  <div className="info-row"><label>Approved At:</label><span>{member.approvedAt ? new Date(member.approvedAt).toLocaleString() : '-'}</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                    <label style={{fontWeight:'bold',minWidth:'100px'}}>Expiry:</label>
                    <input type="datetime-local" value={expiryInput} onChange={e => setExpiryInput(e.target.value)}
                      style={{padding:'6px 8px',border:'1px solid #ccc',borderRadius:'4px',fontSize:'14px'}} />
                    <button onClick={handleExpiryUpdate} style={{padding:'6px 14px',backgroundColor:'#1565c0',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px'}}>
                      Update Expiry
                    </button>
                    {member.expiryDate && new Date(member.expiryDate) < new Date() && (
                      <span style={{color:'#f44336',fontWeight:'bold',fontSize:'13px'}}>⚠️ Expired</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {userRole === 'MASTER' && (
            <div style={{marginTop:'20px',borderTop:'1px solid #ddd',paddingTop:'20px'}}>
              <h3 style={{margin:'0 0 12px', color:'#c62828'}}>Danger Zone</h3>
              {member.deleted ? (
                <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
                  <span style={{padding:'6px 14px',borderRadius:'4px',fontSize:'13px',fontWeight:'bold',background:'#c62828',color:'#fff'}}>
                    🗑 Marked as Deleted
                  </span>
                  <button onClick={handleRestore} disabled={deleteBusy}
                    style={{padding:'6px 14px',backgroundColor:'#1565c0',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px',opacity:deleteBusy?0.6:1}}>
                    {deleteBusy ? '⏳ Restoring...' : '↺ Restore Member'}
                  </button>
                  {member.deletedBy && <span style={{fontSize:'12px',color:'#666'}}>Deleted by <strong>{member.deletedBy}</strong>{member.deletedAt ? ` on ${new Date(member.deletedAt).toLocaleString()}` : ''}</span>}
                </div>
              ) : (
                <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
                  <button onClick={handleMarkDeleted} disabled={deleteBusy}
                    style={{padding:'6px 14px',backgroundColor:'#c62828',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer',fontSize:'13px',opacity:deleteBusy?0.6:1}}>
                    {deleteBusy ? '⏳ Working...' : '🗑 Mark as Deleted'}
                  </button>
                  <span style={{fontSize:'12px',color:'#666'}}>Hides the member from the list and blocks verification. Reversible.</span>
                </div>
              )}
            </div>
          )}

          {idCardUrl && (
            <div style={{marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '20px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <h3 style={{margin: 0}}>Member ID Card</h3>
                <button className="btn btn-primary" onClick={handleDownload}>⬇ Download ID Card</button>
              </div>
              <img src={idCardUrl} alt="ID Card" style={{width: '100%', borderRadius: '8px', border: '1px solid #ddd'}} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MemberDetailModal;
